import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribeCache } from '../cacheBus';

/**
 * Stale-while-revalidate cache para hooks de datos.
 *
 * Comportamiento:
 *   1. Al mount: hidrata `data` sincrónicamente desde sessionStorage si hay
 *      entry < ttl. UI muestra contenido sin spinner aunque el browser haya
 *      descargado la pestaña hace minutos.
 *   2. En paralelo dispara `fetcher()`. Al completar, actualiza
 *      sessionStorage + state. Si el resultado coincide con el cacheado, el
 *      diff de React no rerenderiza.
 *   3. Si la cache está vencida o ausente, `loading=true` y comportamiento
 *      idéntico a un fetch fresco.
 *
 * sessionStorage (no localStorage) porque queremos que la cache muera al
 * cerrar el browser — son datos que pueden cambiar y queremos refresh real
 * en cada nueva sesión, pero NO en cada cambio de pestaña (que es lo común).
 *
 * `key` debe ser estable. `fetcher` debe ser estable también (envolver en
 * useCallback). Si el caller necesita refetch manual, usa `refetch()`.
 */
export function useCachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: { ttlMs?: number; enabled?: boolean } = {},
): { data: T | null; loading: boolean; error: Error | null; refetch: () => Promise<void> } {
  // Default 30s. Antes era 60s pero ahora tenemos realtime + cacheBus
  // invalidando en cada mutación, así que el TTL solo cubre el caso
  // "Safari Memory Saver desuspende la pestaña" — para eso 30s sobran y
  // mantienen los datos más frescos cuando el realtime falla (websocket
  // caído, network restrictivo).
  const { ttlMs = 30_000, enabled = true } = options;
  const storageKey = `aosenuma-cache:${key}`;

  const [state, setState] = useState<{ data: T | null; loading: boolean; error: Error | null }>(() => {
    if (!enabled) return { data: null, loading: false, error: null };
    if (typeof window === 'undefined') return { data: null, loading: true, error: null };
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { ts?: number; data?: T };
        if (parsed.ts != null && Date.now() - parsed.ts < ttlMs && parsed.data !== undefined) {
          // Hit: hidratación sincrónica, sin loading.
          return { data: parsed.data as T, loading: false, error: null };
        }
      }
    } catch {
      // Cache corrupta — ignoramos y vamos a fetch fresco.
    }
    return { data: null, loading: true, error: null };
  });

  const run = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    // Retry con backoff específicamente para AbortError del Web Lock de
    // gotrue-js ("Lock was stolen by another request"). Bug Diego 2026-05-11:
    // en CapturaPage del cliente Elawan corren ~6 hooks de Supabase en
    // paralelo al mount; los que pierden la carrera por el auth lock reciben
    // AbortError y el fetcher lanza antes de completar. El consumer
    // (p. ej. el dropzone "Contrato firmado" que depende de
    // useExpedientesList) ve `data = []` y la UI queda vacía aunque los
    // archivos existen en R2.
    //
    // Estrategia: si `fetcher()` lanza AbortError, esperamos backoff
    // exponencial (250ms → 750ms → 2s) y reintentamos hasta 3 veces. Tras
    // los 3 intentos persistimos el error. Errores NO-AbortError (4xx, 5xx,
    // network) se propagan inmediatamente — no son race conditions.
    const isAbortError = (err: unknown): boolean => {
      if (!(err instanceof Error)) return false;
      // DOMException.name === 'AbortError' o mensaje gotrue específico.
      if (err.name === 'AbortError') return true;
      return /lock.*was.*stolen|aborted|lock.*acquir/i.test(err.message);
    };

    const delays = [250, 750, 2000]; // total ≤ 3s antes de propagar error
    let lastErr: unknown = null;
    for (let attempt = 0; attempt <= delays.length; attempt++) {
      try {
        const data = await fetcher();
        try {
          window.sessionStorage.setItem(storageKey, JSON.stringify({ ts: Date.now(), data }));
        } catch {
          // QuotaExceeded u otro: no fatal, la cache se descarta.
        }
        setState({ data, loading: false, error: null });
        return;
      } catch (e) {
        lastErr = e;
        if (!isAbortError(e) || attempt === delays.length) break;
        // eslint-disable-next-line no-console
        console.warn(
          `[useCachedFetch:${key}] AbortError attempt ${attempt + 1}/${delays.length + 1} — retrying in ${delays[attempt]}ms`,
        );
        await new Promise((res) => window.setTimeout(res, delays[attempt]));
      }
    }
    setState((prev) => ({
      data: prev.data, // keep stale data on error
      loading: false,
      error: lastErr instanceof Error ? lastErr : new Error(String(lastErr)),
    }));
  }, [enabled, fetcher, storageKey, key]);

  // Reset cuando el key cambia: descarta datos viejos para evitar que
  // consumidores apliquen state stale a un contexto distinto. Sin esto,
  // p. ej. useCapturaHydrate('HUM-P1126') → hidrata form con datos de
  // 1126; el user cambia a HUM-P0904 → key cambia pero state.data sigue
  // siendo el de 1126 hasta que el fetch nuevo termine, y los effects
  // del consumer rellenan con datos del propietario equivocado.
  //
  // Comportamiento nuevo: al cambiar el key, intentamos re-hidratar
  // sincronamente desde sessionStorage si hay cache fresca para el
  // nuevo key (mismo patrón que el initializer). Si no, ponemos loading
  // y limpiamos data hasta que llegue el fetch.
  const lastKeyRef = useRef<string>(storageKey);
  useEffect(() => {
    if (lastKeyRef.current === storageKey) return;
    lastKeyRef.current = storageKey;
    if (!enabled) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    if (typeof window !== 'undefined') {
      try {
        const raw = window.sessionStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw) as { ts?: number; data?: T };
          if (parsed.ts != null && Date.now() - parsed.ts < ttlMs && parsed.data !== undefined) {
            setState({ data: parsed.data as T, loading: false, error: null });
            return;
          }
        }
      } catch { /* corrupta — fall through */ }
    }
    // No hay cache fresca para el nuevo key → mostrar loading sin data
    // stale del key anterior.
    setState({ data: null, loading: true, error: null });
  }, [storageKey, enabled, ttlMs]);

  useEffect(() => {
    if (!enabled) return;
    void run();
  }, [enabled, run]);

  // Escuchar invalidaciones del cacheBus. Si esta key está en el mensaje
  // (de esta pestaña o de otra), refetchamos para reflejar el cambio. Sin
  // esto, tras una captura en /equipo el cliente Elawan en otra pestaña
  // veía datos viejos hasta que expirara el TTL de sessionStorage.
  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = subscribeCache((msg) => {
      if (msg.keys.includes(key)) {
        void run();
      }
    });
    return unsubscribe;
  }, [enabled, key, run]);

  return { ...state, refetch: run };
}
